import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import { useRouter } from 'next/router';
import NextNprogress from 'nextjs-progressbar';
import { useEffect, useState } from 'react';
import { SSRProvider } from 'react-bootstrap';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import sal from 'sal.js';
import Layout from '../components/Layout/Layout';
import { DocProvider } from '../context/DocContext';
import '../styles/index.scss';
import '../styles/tailwind.css';
import getTitle from '../utils/getTitle';
import { useLogWeightlossTracks } from '../hooks/useLogWeightlossTracks';

const ChatWidget = dynamic(() => import('../chatwidget/widget/ChatWidget'), {
  ssr: false,
});
const CookieBanner = dynamic(
  () => import('../components/Layout/CookieBanner'),
  { ssr: false }
);

config.autoAddCss = false;

import {
  handleRouteChange as posthogRouteChange,
  identifyUser,
  initPostHog,
  stopPostHog,
} from '../utils/posthogClient';
import { useDocContext } from '../context/DocContext';

function isTrackedRoute(pathname) {
  if (pathname.startsWith('/dashboard')) return false;
  if (pathname === '/login' || pathname === '/signup') return false;
  if (pathname.startsWith('/diagnosis')) {
    if (pathname === '/diagnosis/checkout') return true;
    if (pathname.endsWith('/product_recommendation')) return true;
    return false;
  }
  return true;
}


/**
 * Ties the PostHog person to the account once one is known. Lives inside
 * DocProvider because _app renders the provider and so cannot read it, and
 * sits in one place so sign-up, log in and a restored session are all
 * covered. Only the internal id is sent; identifyUser handles the rest.
 */
function PostHogIdentity() {
  const { user } = useDocContext();
  const userId = user?.objectId;

  useEffect(() => {
    if (userId) identifyUser(userId);
  }, [userId]);

  return null;
}

function MyApp({ Component, pageProps }) {
  let router = useRouter();
  useLogWeightlossTracks(router.asPath);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const shouldTrack = isTrackedRoute(router.pathname) && consentAccepted;
  //Modal popup states
  // let isWeightModal = router?.asPath.includes('EPzuICJy88') ? true : false;
  // const [showOffer, setshowOffer] = useState(false);
  const [acceptSrc, setAcceptSrc] = useState(null);
  const [pageKey, setPageKey] = useState(router.asPath);

  useEffect(() => {
    const match = document.cookie.match(/(?:^|; )cookie_consent=([^;]*)/);
    const val = match ? decodeURIComponent(match[1]) : null;
    setConsentAccepted(val === 'accepted');
  }, []);

  // PostHog is deliberately NOT behind isTrackedRoute the way the ad tags
  // are. It runs on the patient pages too, protected by per-area masking
  // rather than by exclusion — see utils/posthogPolicy.js. Consent still
  // gates it, and nothing starts until the visitor has accepted.
  useEffect(() => {
    if (consentAccepted) {
      initPostHog();
    } else {
      stopPostHog();
    }
  }, [consentAccepted]);

  useEffect(() => {
    const onRouteChangeStart = (url) => {
      const pathname = new URL(url, window.location.origin).pathname;
      if (!isTrackedRoute(pathname) && isTrackedRoute(router.pathname)) {
        // The throw below aborts the navigation, so routeChangeStart
        // listeners registered after this one never hear about it. Announce
        // it first for anything that records where visitors go
        // (utils/subscribeTracking.js).
        window.dispatchEvent(
          new CustomEvent('app:hard-navigation', { detail: url })
        );
        window.location.href = url;
        // eslint-disable-next-line no-throw-literal
        throw 'routeChange aborted — hard reload into protected route';
      }
    };
    router.events.on('routeChangeStart', onRouteChangeStart);
    return () => router.events.off('routeChangeStart', onRouteChangeStart);
  }, [router.pathname, router.events]);

  useEffect(() => {
    const onRouteChangeComplete = (url) => {
      setPageKey(url);
      const pathname = new URL(url, window.location.origin).pathname;
      const allowed = isTrackedRoute(pathname) && consentAccepted;

      // Next's client side routing does not reload the page, so PostHog has
      // to be told, and the recorder started or stopped for the new area.
      if (consentAccepted) posthogRouteChange(pathname);

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'routeChange',
          trackingAllowed: allowed,
          pagePath: pathname,
        });
      }

      if (window.uetq) {
        window.uetq.push = allowed
          ? window._originalUetqPush || window.uetq.push
          : () => {};

        // Manual SPA page view for Bing UET. Auto SPA tracking
        // (enableAutoSpaTracking) is intentionally OFF: it patches
        // history.pushState/replaceState and drops Next's __N history
        // state, which broke browser back/forward across the site.
        if (allowed) {
          window.uetq.push('event', 'page_view', { page_path: pathname });
        }
      }
    };

    router.events.on('routeChangeComplete', onRouteChangeComplete);
    return () =>
      router.events.off('routeChangeComplete', onRouteChangeComplete);
  }, [router.events, consentAccepted]);

  // Guard Next's history state against third-party trackers.
  // GeniusMonkey (gm.js) calls history.replaceState({}, '', <same url>)
  // after load, wiping the { __N, url, as, key } state Next stores on the
  // entry. Next's popstate handler then ignores the entry, so browser
  // back/forward changes the URL without re-rendering the page. When a
  // same-URL replaceState arrives without __N while the current entry has
  // it, keep the existing router state instead of letting it be erased.
  useEffect(() => {
    const origReplace = window.history.replaceState.bind(window.history);
    window.history.replaceState = function (state, title, url) {
      const cur = window.history.state;
      let sameUrl = url == null;
      if (!sameUrl) {
        try {
          sameUrl =
            new URL(url, window.location.href).href === window.location.href;
        } catch {
          sameUrl = false;
        }
      }
      if (cur && cur.__N && sameUrl && (!state || !state.__N)) {
        state = cur;
      }
      return origReplace(state, title, url);
    };
    return () => {
      window.history.replaceState = origReplace;
    };
  }, []);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Lato:ital,wght@0,300;0,400;1,700&family=Montserrat:wght@300;400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const host = window.location.host;

    const isProduction = host === 'www.24hrdoc.com' || host === '24hrdoc.com';

    setAcceptSrc(
      isProduction
        ? 'https://js.authorize.net/v1/Accept.js' // PROD
        : 'https://jstest.authorize.net/v1/Accept.js' // SANDBOX
    );
  }, []);
  const triggerViewEvent = () => {
    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'customPageViewEvent',
          pageUrl: window.location.href,
          pageTitle: getTitle(window.location.pathname),
        });
        clearInterval(interval);
      }
    }, 100 * 5); // Retry every 500ms
  };
  // useEffect(() => {
  //   const initRevechat = () => {
  //     if (window.__revechat_initialized) return;
  //     window.__revechat_initialized = true;

  //     const rc = document.createElement('script');
  //     rc.src =
  //       'https://static.revechat.com/widget/scripts/new-livechat.js?' +
  //       Date.now();
  //     rc.async = true;
  //     rc.charset = 'utf-8';
  //     document.body.appendChild(rc);

  //     window.__revechat_account = '7294519';
  //     window.__revechat_version = 2;
  //     window.$_REVECHAT_API = function (c) {
  //       $_REVECHAT_API._.push(c);
  //     };
  //     $_REVECHAT_API._ = [];

  //     window.removeEventListener('scroll', initRevechat);
  //   };

  //   window.addEventListener('scroll', initRevechat);

  //   return () => window.removeEventListener('scroll', initRevechat);
  // }, []);
  useEffect(() => {
    const handleUnhandledRejection = (e) => {
      console.error('[unhandledrejection] reason:', e.reason);
      console.error('[unhandledrejection] url:', window.location.href);
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () =>
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
  }, []);

  useEffect(() => {
    sal({
      threshold: 0.5,
      once: false,
    });
    triggerViewEvent();
    getUTMParamsAndStoreUrl();
    window.scrollTo(0, 0);
  }, [router.asPath]);
  //Weight loss popup
  // useEffect(() => {
  //   setTimeout(() => {
  //     if (
  //       !router.asPath.includes('/diagnosis') &&
  //       sessionStorage.getItem('offerShown') !== 'true'
  //     ) {
  //       setshowOffer(true);
  //       sessionStorage.setItem('offerShown', 'true');
  //     } else return;
  //   }, 20 * 1000);
  // }, [router.asPath]);
  const options = {
    // passing the client secret obtained from the server
    clientSecret: '{{CLIENT_SECRET}}',
  };

  const logConsent = (choice) =>
    fetch('/api/consent/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ choice }),
    })
      .then((r) => r.json())
      .then(({ objectId }) => {
        if (objectId) localStorage.setItem('consent_log_id', objectId);
      })
      .catch(() => {});

  return (
    // <GoogleOAuthProvider clientId='257540012081-sm9h9g9rvcqt9rsdga2smg5gj0piva7g.apps.googleusercontent.com'>
    <DocProvider>
      <PostHogIdentity />
      <SSRProvider>
        {acceptSrc && <Script src={acceptSrc} strategy='afterInteractive' />}
          <CookieBanner
            onAccept={() => {
              setConsentAccepted(true);
              logConsent('accepted');
            }}
            onReject={() => {
              setConsentAccepted(false);
              logConsent('rejected');
              if (window.dataLayer)
                window.dataLayer.push({
                  event: 'routeChange',
                  trackingAllowed: false,
                });
              if (window.uetq) window.uetq.push = () => {};
            }}
          />
          {/* <PayPalScriptProvider
            options={{
              'client-id': process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
            }}
            deferLoading={true}
          > */}
          <Head>
            <title>24Hrdoc</title>
          </Head>

          <Layout layout={pageProps.layout}>
            {/* <Modal
              size='lg'
              show={showOffer}
              onHide={() => setshowOffer(false)}
              className={`modalOffer ${
                isWeightModal ? 'weightModal' : 'normalModal'
              }`}
              centered
            >
              <PopupBody
                setshowOffer={setshowOffer}
                isWeightModal={isWeightModal}
              />
            </Modal> */}
            {shouldTrack && (
              <>
                <noscript>
                  <iframe
                    src='https://www.googletagmanager.com/ns.html?id=GTM-P9G9VKL'
                    height='0'
                    width='0'
                    style={{ display: 'none', visibility: 'hidden' }}
                  />
                </noscript>
                <Script
                  id='gtm'
                  strategy='afterInteractive'
                  dangerouslySetInnerHTML={{
                    __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-P9G9VKL');`,
                  }}
                />
                <Script
                  id='bing-uet'
                  strategy='afterInteractive'
                  dangerouslySetInnerHTML={{
                    __html: `(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[];f=function(){var o={ti:"187154976"};o.q=w[u];w[u]=new UET(o);w[u].push("pageLoad")};n=d.createElement(t);n.src=r;n.async=1;n.onload=n.onreadystatechange=function(){var s=this.readyState;if(!s||s==="loaded"||s==="complete"){f();n.onload=n.onreadystatechange=null}};i=d.getElementsByTagName(t)[0];i.parentNode.insertBefore(n,i)})(window,document,"script","https://bat.bing.com/bat.js","uetq");`,
                  }}
                />
                {/* All three tag scripts stay on afterInteractive by
                    deliberate choice. Deferring them to lazyOnload would
                    shave main-thread time during hydration, but ad
                    performance attribution is business-critical here and
                    lazyOnload can skip the script entirely on a fast bounce.
                    Do not "optimise" these to lazyOnload without owner
                    sign-off — the cost is silently lost conversions. */}
                <Script
                  strategy='afterInteractive'
                  type='text/javascript'
                  async
                  src='https://pm.geniusmonkey.com/gm.js?id=1028352058&z=1'
                />
              </>
            )}
            <Component key={pageKey} {...pageProps} />
            <ChatWidget />
          </Layout>
          <ToastContainer
            position='bottom-right'
            autoClose={5000}
            closeOnClick
          />
          <NextNprogress height={5} startPosition={0.5} />

          {/* <LiveChatWidget
          license={process.env.NEXT_PUBLIC_LIVECHAT}
          visibility='minimized'
          onNewEvent={handleNewEvent}
        /> */}
          {/* </PayPalScriptProvider> */}
      </SSRProvider>
    </DocProvider>
    // </GoogleOAuthProvider>
  );
}

export default MyApp;
function getUTMParamsAndStoreUrl() {
  const currentUrl = window.location.href;
  const params = new URLSearchParams(window.location.search);

  // Regex pattern to match services URLs with dynamic parts like /services/{id}/{condition}
  const urlPattern =
    /^https:\/\/www\.24hrdoc\.com\/services\/[A-Za-z0-9]+\/[A-Za-z\-]+/;

  // Check if URL matches the pattern and has query parameters
  if (urlPattern.test(currentUrl) && currentUrl.includes('?')) {
    // Store the full URL in localStorage
    localStorage.setItem('storedUrl', currentUrl);
  }

  // Extract UTM parameters from the URL
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  // Store UTM parameters in localStorage if they exist
  if (utmSource) {
    localStorage.setItem('utm_source', utmSource);
  }
  if (utmMedium) {
    localStorage.setItem('utm_medium', utmMedium);
  }
  if (utmCampaign) {
    localStorage.setItem('utm_campaign', utmCampaign);
  }

  // Optionally set a flag for 'purchased' in localStorage
  localStorage.setItem('purchased', JSON.stringify(false));
}
